import { ExportOutlined } from '@ant-design/icons';
import { Button, Card, Empty, Select, Space, Spin, Table, Typography } from 'antd';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { BsBox2 } from 'react-icons/bs';
import { MdHistory } from 'react-icons/md';

import PPTable from '../../components/table/PPTable';
import useSearchParams from '../../components/table/useSearchParams';
import { getFormattedResponse } from '../../utils';
import { getTableState } from '../../utils/localStorage';
import './Product.less';

const { Text } = Typography;

const tablePrefix = 'product_stock';

/** Картын толгой: дүрс + текст */
const CardTitle = ({ icon: Icon, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
    <Icon style={{ display: 'block', fontSize: '1em' }} />
    {children}
  </span>
);

export default function Index({ detail = {}, onExport, exporting = false, customizeColumns }) {
  const [data, setData] = useState({});
  const [stockLoading, setStockLoading] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseCode, setWarehouseCode] = useState();
  const [tableState, setTableState] = useState(() => {
    const { page = 1, pageSize = 10, totalDocs = 0 } = getTableState(tablePrefix);
    return {
      columns: [],
      filteredKeys: [],
      docs: [],
      page,
      pageSize,
      totalDocs,
    };
  });

  useEffect(() => {
    if (detail.productSku) {
      fetchStocks(warehouseCode);
    }
    //eslint-disable-next-line
  }, [warehouseCode, detail.productSku]);

  useEffect(() => {
    if (warehouseCode) {
      fetchProductLog();
    }
    //eslint-disable-next-line
  }, [warehouseCode]);

  useEffect(() => {
    fetchProductLog();
    //eslint-disable-next-line
  }, []);

  const handleChangeTable = (tableState = {}) => {
    setTableState(tableState);
  };

  const fetchProductLog = async (params) => {
    const { page, pageSize } = tableState;
    const payload = {
      page,
      pageSize,
      productSku: detail.productSku,
      wareHouseCode: warehouseCode,
      ...params,
    };
    setLogLoading(true);
    try {
      const { data } = await axios.post(`/api/v1.0/product/listproductlog`, payload);
      const { columns, list, pageSize, page, totalDocs } = getFormattedResponse(data);
      let filteredList = list.filter((item) => item.isShow !== false);
      setTableState((prev) => ({
        ...prev,
        columns: columns,
        docs: filteredList,
        pageSize,
        page,
        totalDocs,
      }));
    } catch (e) {
      console.error('Product log fetch error: ', e);
    } finally {
      setLogLoading(false);
    }
  };

  const searchQuery = useSearchParams({ fetchProductLog });

  const fetchStocks = async (warehouseCode) => {
    setStockLoading(true);
    try {
      const { data } = await axios.post('/api/v1.0/product/stock', {
        warehouseCode,
        eventOrID: detail.productSku,
      });
      if (data.statusCode === 200) {
        if (warehouseCode) {
          setData(data.result);
        } else {
          setWarehouses(data.result.warehouses);
          setWarehouseCode();
          setData({});
        }
      }
    } finally {
      setStockLoading(false);
    }
  };

  /** Агуулахын код -> нэр */
  const warehouseName = (code) => warehouses.find((opn) => opn.value === code)?.label ?? code;

  const columns = [
    {
      key: 'warehouse',
      dataIndex: 'warehouseCode',
      title: 'Агуулахын нэр',
      align: 'left',
      width: 200,
      render: (text) => warehouseName(text),
    },
    {
      key: 'stock',
      dataIndex: 'stock',
      title: 'Үлдэгдэл',
      align: 'center',
      width: 200,
      render: (text) => <Text strong>{text ?? 0}</Text>,
    },
  ];

  const stocks = data?.stocks ?? [];

  const stockSummary = (rows = []) => (
    <Table.Summary.Row>
      <Table.Summary.Cell align="center">
        <Text>Нийт</Text>
      </Table.Summary.Cell>
      <Table.Summary.Cell align="center">
        <Text strong>{rows.reduce((total, row) => total + (Number(row?.stock) || 0), 0)}</Text>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
      <Card
        title={<CardTitle icon={BsBox2}>Агуулахын үлдэгдэл</CardTitle>}
        size="middle"
        extra={
          <Select
            options={warehouses}
            value={warehouseCode}
            onChange={(value) => setWarehouseCode(value)}
            placeholder="Агуулах сонгох"
            loading={stockLoading}
            allowClear
            style={{ minWidth: 220 }}
          />
        }
        styles={{ body: { padding: stocks.length ? 0 : undefined } }}
      >
        <Spin spinning={stockLoading}>
          <div style={{ padding: '15px' }}>
            {stocks.length > 0 ? (
              <Table
                size="large"
                rowKey={(record, index) => record?.warehouseCode ?? index}
                columns={columns}
                dataSource={stocks}
                pagination={false}
                summary={stockSummary}
                scroll={{ x: 'max-content' }}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={warehouseCode ? 'Үлдэгдэл байхгүй байна.' : 'Агуулах сонгоно уу.'}
                style={{ margin: 0 }}
              />
            )}
          </div>
        </Spin>
      </Card>

      <Card
        title={<CardTitle icon={MdHistory}>Өөрчлөлтийн түүх</CardTitle>}
        size="middle"
        extra={
          onExport && (
            <Button
              type="primary"
              icon={<ExportOutlined />}
              loading={exporting}
              onClick={onExport}
            >
              Export
            </Button>
          )
        }
        styles={{ body: { padding: 0 } }}
      >
        <PPTable
          tableState={tableState}
          tablePrefix={tablePrefix}
          handleChangeTable={handleChangeTable}
          fetchList={fetchProductLog}
          searchQuery={searchQuery}
          customizeColumns={customizeColumns}
          loading={logLoading}
        />
      </Card>
    </Space>
  );
}
